import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import store from './app/store';
import Routes from './Routes';
import { initializeAuth, fetchCurrentUser, selectIsAuthenticated } from './app/slices/authSlice';
import { fetchSubscription } from './app/slices/subscriptionSlice';
import { fetchEnabledModules } from './app/slices/enabledModulesSlice';
import { selectTheme } from './app/slices/uiSlice';
import UserbackWidgetInitializer from './shared/integrations/UserbackWidgetInitializer';
import PropTypes from 'prop-types';

// App initializer component
export function AppInitializer({ children }) {
  const dispatch = useDispatch();
  const theme = useSelector(selectTheme);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    // Initialize authentication state from sessionStorage + restore user from server
    dispatch(initializeAuth());

    // Fetch current user from /api/v1/auth/users/me/ to restore state from valid cookie session
    // This validates the cookie session and restores user/permissions state
    dispatch(fetchCurrentUser()).catch(() => {
      // User not authenticated - this is fine, will show login page
    });
  }, [dispatch]);

  // IDN-17: fetchSubscription/fetchEnabledModules previously only ran once,
  // chained after the initial mount's fetchCurrentUser resolved -- so an
  // in-session OTP/magic-link login, signup completion, or a logout followed
  // by a fresh login never re-ran them, leaving subscription/module gating
  // (Sidebar, ModuleRoute) stale from whatever was true (or empty) at the
  // very first page load. Every one of those transitions sets
  // state.auth.isAuthenticated = true (fetchCurrentUser.fulfilled,
  // login/loginWithToken/verifyOtpLogin.fulfilled, setAuthComplete on
  // signup), so driving this off isAuthenticated covers all of them
  // uniformly, including the original restored-session mount case.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchSubscription());
      dispatch(fetchEnabledModules());
    }
  }, [isAuthenticated, dispatch]);

  // Apply theme class to body
  useEffect(() => {
    document.body.className = `${theme}-theme`;
  }, [theme]);

  const themeClass = theme === 'dark' ? 'dark-theme' : 'light-theme';
  const bgGradient = theme === 'dark' ? 'bg-gradient-dark' : 'bg-gradient-light';

  return (
    <div className={`min-h-screen ${bgGradient} ${themeClass}`}>
      {/* Skip to main content link for keyboard accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:theme-accent-bg focus:theme-text-primary focus:rounded-lg focus:font-medium focus:outline-none focus:ring-2 focus:ring-offset-2"
      >
        Skip to main content
      </a>
      <UserbackWidgetInitializer />
      {children}
    </div>
  );
}

AppInitializer.propTypes = {
  children: PropTypes.node.isRequired,
};

function App() {
  return (
    <Provider store={store}>
      <AppInitializer>
        <Routes />
      </AppInitializer>
    </Provider>
  );
}

export default App;
